/**
 * Queen B Academy - Trucking 101 Enhanced Content
 * Extended lessons with real-world examples, exercises, pro tips, and actionable content
 */

export const TRUCKING_BUSINESS_ENHANCED = {
  courseId: "trucking-101",
  courseTitle: "Trucking 101",
  enhancedModules: {
    // ============================================
    // MODULE 1: STARTING A TRUCKING COMPANY
    // ============================================
    "truck-m1": {
      moduleTitle: "Starting a Trucking Company",
      lessons: {
        "truck-m1-l1": {
          title: "The Trucking Business Opportunity - Complete Industry Analysis",
          duration: "60 min",
          content: `# Starting Your Own Trucking Company: The Complete Roadmap

## Why Now Is the Time

The trucking industry is experiencing a **generational shift** that creates unprecedented opportunity for new owner-operators and small fleet owners:

**The Driver Shortage Reality:**
- Average driver age: 55+ years (approaching retirement)
- Current shortage: 80,000+ drivers
- Projected shortage by 2030: 160,000 drivers
- Freight demand continuing to grow

**E-Commerce Explosion:**
- Amazon, Walmart, and direct-to-consumer shipping exploding
- "Buy online, ship anywhere" creates endless freight
- Last-mile and middle-mile demand increasing

**Technology Equalizes Small Players:**
- Load boards give small operators same access as mega-fleets
- ELDs standardize compliance for all
- Communication technology levels the playing field

### The Numbers Don't Lie

| Statistic | Value | What It Means |
|-----------|-------|---------------|
| Freight moved by truck | 72.5% of all US freight | Trucks are essential |
| Industry annual revenue | $940.8 billion | Massive market |
| Truck drivers in America | 3.5 million | Large industry |
| Average O/O gross revenue | $250,000-$350,000 | Real money potential |
| Average O/O take-home | $80,000-$150,000 | After expenses |

---

## Owner-Operator vs Fleet Owner: Choose Your Path

### The Owner-Operator Path

You own one truck and drive it yourself.

**Financial Reality:**
- Startup costs: $30,000-$80,000
- Monthly expenses: $8,000-$15,000
- Potential gross: $15,000-$30,000/month
- Net income: $6,000-$12,000/month

**Pros:**
- Lower startup costs
- Full control over your schedule
- All profit stays with you
- No employee management headaches
- Learn the business hands-on

**Cons:**
- Income stops when you stop driving
- You ARE the business - no backup
- Limited growth potential
- Must drive to earn
- Can't take sick days easily

**Best For:**
- Experienced CDL drivers wanting independence
- Those who love driving and being on the road
- People who want lower risk entry
- Those who don't want to manage others

### The Fleet Owner Path

You own trucks and hire drivers to operate them.

**Financial Reality:**
- Startup costs: $100,000-$250,000
- Monthly expenses per truck: $12,000-$18,000
- Potential gross per truck: $18,000-$28,000/month
- Net per truck (with hired driver): $2,000-$6,000/month

**Pros:**
- Scalable income - more trucks = more revenue
- Business runs without you driving
- Build equity in assets
- Multiple revenue streams
- True business ownership

**Cons:**
- Higher startup costs and risk
- Employee management challenges
- More complex operations
- Driver turnover is real
- More things can go wrong

**Best For:**
- Business-minded individuals
- Those with capital or financing capability
- People who want to build something bigger
- Former managers or business owners

---

## The True Cost of Starting Up

### Minimum Viable Trucking Company

**Used Truck Route (Lowest Cost Entry)**

| Item | Cost Range | Notes |
|------|------------|-------|
| Used truck (5-8 years old) | $30,000-$55,000 | Or $5,000-$15,000 down if financing |
| Insurance (quarterly) | $4,500-$8,000 | Higher for new authority |
| MC Authority filing | $300 | FMCSA fee |
| BOC-3 Process Agent | $30-$75 | One-time filing |
| UCR Registration | $69 | Annual for 1-2 trucks |
| IFTA setup | $0-$50 | State dependent |
| ELD device | $150-$500 | Plus monthly fees |
| Operating capital | $10,000-$20,000 | CRITICAL - don't skip |
| **TOTAL MINIMUM** | **$25,000-$45,000** | |

**New Truck Route (Higher Investment)**

| Item | Cost Range | Notes |
|------|------------|-------|
| New truck (down payment) | $15,000-$35,000 | With financing |
| Insurance (quarterly) | $5,000-$10,000 | Slightly higher for new trucks |
| Authority & registrations | $500 | All filings |
| ELD & safety tech | $500-$1,500 | More advanced systems |
| Operating capital | $15,000-$30,000 | More buffer needed |
| **TOTAL MINIMUM** | **$45,000-$80,000** | |

### Hidden Costs Nobody Tells You About

**These will hit you - plan for them:**

| Hidden Cost | Typical Amount | When It Hits |
|-------------|----------------|--------------|
| Bobtail insurance | $50-$125/month | Monthly |
| Deadhead miles | 10-15% of revenue | Every load |
| Maintenance reserve | $0.15-$0.25/mile | Ongoing |
| Fuel price spikes | Variable | Unpredictable |
| Detention (unpaid) | $200-$500/occurrence | Random |
| Tire replacement | $400-$700/tire | Every 100K miles |
| Unexpected repairs | $500-$5,000+ | Random |
| Lumper fees (unreimbursed) | $100-$400/occurrence | Certain loads |
| Permit fees | $500-$2,000/year | Annual |
| Escrow for taxes | 25-30% of profit | Quarterly |

**Real-World Example:**

"I started with exactly $40,000. Within 90 days, I had spent $38,000 and had my first breakdown - $2,800 for a turbo replacement. I had to borrow from family to cover it. Start with MORE than you think you need." - First-year O/O

---

## Authority: Your License to Operate

### What You Need

**MC Number (Motor Carrier Authority)**
- Required to haul freight for hire
- Costs $300 to apply through FMCSA
- 21-day minimum processing time
- Brokers verify this before giving you loads
- Goes "active" only after insurance is filed

**USDOT Number**
- Required for all commercial vehicles
- Free to obtain
- Ties to your safety record (CSA scores)
- Displayed on your truck doors
- Must be renewed every 2 years

**UCR (Unified Carrier Registration)**
- Annual registration required
- Fee based on fleet size ($69+ for 1-2 trucks)
- Must be current before operating
- Renew before October 1 each year

**IFTA (International Fuel Tax Agreement)**
- Quarterly fuel tax reporting
- Apply through your base state
- Report miles and fuel purchased in each state
- Due last day of month after quarter ends

**BOC-3 (Process Agent)**
- Designates agent to receive legal papers in each state
- Required before authority activates
- Costs $30-$100 (blanket service recommended)
- One-time filing unless agent changes

### The Authority Timeline

| Day | Action | Status |
|-----|--------|--------|
| Day 1 | Apply for MC/DOT at FMCSA portal | Application submitted |
| Day 3-5 | BOC-3 filing submitted | Processing |
| Day 7-14 | Shop insurance quotes | Active |
| Day 14-18 | Select insurance, bind coverage | Coverage active |
| Day 18-21 | Insurance filing sent to FMCSA | Processing |
| Day 21-25 | Authority shows "Active" on SAFER | **Ready to operate** |

**Pro Tip:** Start insurance shopping on Day 1, not Day 14. The best rates take time to quote.

### Queen B BUILD Program Advantage

The Queen B BUILD tier handles all authority setup:
- MC/DOT application assistance
- BOC-3 filing
- UCR registration
- IFTA setup guidance
- Insurance agent introductions
- First load coaching
- Compliance calendar setup

"I was overwhelmed trying to figure out authority myself. Queen B BUILD walked me through everything and had me running freight in 28 days. Worth every penny." - Marcus, O/O since 2023

---

## Insurance: Your Biggest Ongoing Cost

### Required Coverage Types

**Primary Liability ($750,000-$1,000,000)**
- Legally required for operating authority
- Covers damage you cause to others
- Most brokers require $1,000,000 minimum
- **Cost:** $12,000-$20,000/year for new authority

**Cargo Insurance ($100,000 standard)**
- Covers freight damage or loss
- Most brokers require this
- Higher limits for specialized freight
- **Cost:** $1,500-$3,500/year

**Physical Damage**
- Covers YOUR truck in accidents
- Required if you're financing
- Optional but smart if truck is paid off
- **Cost:** 2-5% of truck value annually

**Bobtail Insurance**
- Covers driving without trailer attached
- When you're not dispatched/working
- Often included in liability bundle
- **Cost:** $500-$1,500/year

**Occupational Accident**
- Like workers' comp for O/Os
- Covers injuries while working
- Highly recommended
- **Cost:** $2,000-$5,000/year

### Total First-Year Insurance Reality

**New Authority, Clean Record:**

| Coverage | Annual Cost |
|----------|-------------|
| Primary Liability ($1M) | $14,000-$22,000 |
| Cargo ($100K) | $2,000-$3,500 |
| Physical Damage | $3,000-$6,000 |
| Bobtail | $500-$1,500 |
| Occupational Accident | $2,000-$4,000 |
| **TOTAL** | **$21,500-$37,000** |

Monthly budget: **$1,800-$3,100**

### How to Get Better Insurance Rates

**Immediate Impact:**
- Clean driving record (no violations, accidents)
- Higher deductibles (trade lower premium for more risk)
- Shop multiple specialized trucking insurers
- Pay annually vs. monthly (saves 10-15%)

**Over Time:**
- Build claims-free history (biggest factor after year 2)
- Maintain clean CSA scores
- Add safety technology (cameras, speed limiters)
- Establish business credit
- Join industry associations with group rates

---

## Your First 90 Days: The Critical Period

### Week 1-2: Setup Phase

**Days 1-7:**
- [ ] Truck purchased/leased and road-ready
- [ ] All registrations complete
- [ ] Insurance bound and filed with FMCSA
- [ ] ELD installed and tested
- [ ] Load board subscriptions active
- [ ] Bank account and accounting system ready

**Days 8-14:**
- [ ] Authority verified "Active" on SAFER
- [ ] First loads booked
- [ ] Initial broker relationships started
- [ ] All permits in truck

### Week 3-4: First Loads

**Focus:** Learning, not maximizing revenue

- [ ] Complete 4-6 shorter loads (under 500 miles)
- [ ] Learn load board systems thoroughly
- [ ] Practice rate negotiation
- [ ] Track every expense (build the habit)
- [ ] Identify what's working and what isn't

### Month 2: Building Rhythm

**Focus:** Finding your operational groove

- [ ] Establish 2-3 consistent lanes
- [ ] Improve rate negotiation skills
- [ ] Build broker relationship network
- [ ] Optimize fuel stop strategy
- [ ] Identify profitable vs. unprofitable loads

### Month 3: Optimization

**Focus:** Refining for profitability

- [ ] Analyze Month 1-2 performance data
- [ ] Cut unprofitable lanes/brokers
- [ ] Consider dedicated or contract opportunities
- [ ] Plan any equipment upgrades needed
- [ ] Evaluate: Is this sustainable?

---

## Exercise: Startup Budget Worksheet

Calculate YOUR realistic startup needs:

### Equipment

| Item | Your Research | Your Budget |
|------|---------------|-------------|
| Truck (purchase/down payment) | | $ |
| Trailer (if needed) | | $ |
| ELD device | | $ |
| Safety equipment | | $ |
| Miscellaneous equipment | | $ |
| **Equipment Total** | | **$** |

### Authority & Compliance

| Item | Standard Cost | Your Budget |
|------|---------------|-------------|
| MC/DOT filing | $300 | $ |
| BOC-3 | $30-75 | $ |
| UCR | $69 | $ |
| IFTA setup | $0-50 | $ |
| State permits | varies | $ |
| **Compliance Total** | | **$** |

### Insurance (First Quarter)

| Item | Quote 1 | Quote 2 | Quote 3 |
|------|---------|---------|---------|
| Liability | $ | $ | $ |
| Cargo | $ | $ | $ |
| Physical Damage | $ | $ | $ |
| Other | $ | $ | $ |
| **Insurance Total** | **$** | **$** | **$** |

### Operating Capital

| Item | Monthly Need | 3-Month Reserve |
|------|--------------|-----------------|
| Fuel | $ | $ |
| Maintenance | $ | $ |
| Communication | $ | $ |
| Personal expenses | $ | $ |
| Emergency fund | $ | $ |
| **Operating Total** | | **$** |

### Grand Total

| Category | Amount |
|----------|--------|
| Equipment | $ |
| Compliance | $ |
| Insurance | $ |
| Operating Capital | $ |
| **TOTAL STARTUP CAPITAL NEEDED** | **$** |

---

## Key Takeaways

- Starting a trucking company requires $25,000-$80,000 minimum capital
- Authority setup takes 21+ days - start early, plan accordingly
- Insurance is your biggest ongoing cost - budget $1,800-$3,100/month first year
- Hidden costs add 20-30% to your estimates - plan conservatively
- The first 90 days establish your foundation - don't rush to maximize revenue
- Operating capital is as important as truck purchase - don't skimp

---

## What's Next?

In the next lesson, we'll break down the FMCSA and DOT regulatory landscape - what you must know to stay compliant and avoid costly violations.`,
          exercises: [
            {
              type: "budget",
              title: "Complete Startup Budget",
              prompt: "Fill out the complete startup budget worksheet with real numbers from your research. Get actual insurance quotes (not estimates) if possible.",
              timeEstimate: "2 hours",
              deliverable: "Completed budget with real numbers"
            },
            {
              type: "research",
              title: "Truck Shopping Research",
              prompt: "Find 5 trucks in your budget range (new or used). Document: Year, make, mileage, price, and calculate estimated insurance cost for each.",
              timeEstimate: "1 hour",
              deliverable: "Truck comparison spreadsheet"
            }
          ],
          keyTakeaways: [
            "Owner-operator startup costs: $25,000-$80,000 depending on truck choice",
            "Insurance costs $15,000-$35,000/year for new authorities",
            "Authority process takes 21+ days minimum - don't wait to start",
            "Operating capital is as critical as equipment purchase",
            "First 90 days should focus on learning, not maximizing revenue"
          ],
          proTips: [
            "Start insurance shopping on Day 1 of your authority application",
            "Build a 3-month operating reserve BEFORE buying your truck",
            "Get 3-5 insurance quotes - rates vary dramatically",
            "Join owner-operator forums to learn from others' costly mistakes"
          ]
        }
      }
    },

    // ============================================
    // MODULE 6: COST PER MILE
    // ============================================
    "truck-m6": {
      moduleTitle: "Cost Per Mile",
      lessons: {
        "truck-m6-l1": {
          title: "Complete Expense Breakdown for Trucking",
          duration: "50 min",
          content: `# Know Your Numbers: Complete Expense Breakdown

## Why Cost Per Mile Is Everything

If you don't know your cost per mile, you're guessing on every load decision. You might be:
- Taking loads that actually lose money
- Turning down profitable loads
- Slowly going broke without realizing it
- Unable to negotiate from strength

Every successful trucking business knows their numbers. Let's break down exactly how to calculate yours.

---

## Fixed Costs (Monthly Expenses)

Fixed costs occur whether you drive 10,000 miles or 1,000 miles.

### Truck Payment

| Scenario | Monthly Payment |
|----------|-----------------|
| Used truck, financed 4 years | $1,200-$1,800 |
| New truck, financed 5 years | $2,200-$3,500 |
| Paid-off truck | $0 (but save for replacement) |

**Depreciation Note:** Even if your truck is paid off, budget $0.10-$0.15/mile for eventual replacement.

### Insurance

| Coverage | Monthly Cost Range |
|----------|-------------------|
| Liability + Cargo | $1,200-$2,000 |
| Physical Damage | $200-$500 |
| Bobtail + OccAcc | $150-$400 |
| **Total Insurance** | **$1,550-$2,900** |

### Permits & Authority

| Item | Annual Cost | Monthly Average |
|------|-------------|-----------------|
| IFTA | $50-$100 | $4-$8 |
| IRP (registration) | $1,500-$3,000 | $125-$250 |
| UCR | $69 | $6 |
| Overweight permits | $200-$500 | $17-$42 |
| HVUT (Form 2290) | $550 | $46 |
| **Total Permits** | | **$200-$350** |

### Communication & Technology

| Item | Monthly Cost |
|------|--------------|
| Cell phone | $80-$150 |
| ELD subscription | $25-$50 |
| Load board subscription | $40-$200 |
| GPS/mapping | $0-$30 |
| **Total Tech** | **$145-$430** |

### Fixed Cost Summary

| Category | Low Estimate | High Estimate |
|----------|--------------|---------------|
| Truck Payment | $1,200 | $3,500 |
| Insurance | $1,550 | $2,900 |
| Permits | $200 | $350 |
| Technology | $145 | $430 |
| Parking/storage | $0 | $300 |
| **TOTAL FIXED** | **$3,095** | **$7,480** |

---

## Variable Costs (Per-Mile Expenses)

Variable costs increase directly with miles driven.

### Fuel (Your Biggest Variable)

**Calculating Fuel Cost Per Mile:**
- Know your MPG (track this weekly)
- Track current fuel prices
- Formula: Fuel price ÷ MPG = Cost per mile

| MPG | $3.50/gal | $4.00/gal | $4.50/gal |
|-----|-----------|-----------|-----------|
| 5.5 MPG | $0.64/mile | $0.73/mile | $0.82/mile |
| 6.0 MPG | $0.58/mile | $0.67/mile | $0.75/mile |
| 6.5 MPG | $0.54/mile | $0.62/mile | $0.69/mile |
| 7.0 MPG | $0.50/mile | $0.57/mile | $0.64/mile |
| 7.5 MPG | $0.47/mile | $0.53/mile | $0.60/mile |

**Your Calculation:**
Current fuel price: $_____/gallon
Your actual MPG: _____ MPG
Your fuel cost: $_____ per mile

### Maintenance

| Type | Cost Per Mile | Notes |
|------|---------------|-------|
| Routine (oil, filters) | $0.03-$0.05 | Preventative |
| Tires | $0.04-$0.06 | Replacement over time |
| Major repairs reserve | $0.08-$0.12 | Engine, trans, etc. |
| **Total Maintenance** | **$0.15-$0.23** | |

### Tolls

| Region | Typical Cost Per Mile |
|--------|----------------------|
| Northeast corridor | $0.08-$0.15 |
| Midwest | $0.02-$0.05 |
| Southeast | $0.03-$0.06 |
| West | $0.02-$0.04 |
| Average | **$0.04-$0.07** |

### DEF (Diesel Exhaust Fluid)

Most modern trucks use 2-3% of fuel consumption in DEF.
- Typical cost: $0.01-$0.02 per mile

### Variable Cost Summary

| Category | Low Estimate | High Estimate |
|----------|--------------|---------------|
| Fuel (at 6.5 MPG, $4/gal) | $0.62 | $0.75 |
| Maintenance | $0.15 | $0.23 |
| Tolls | $0.04 | $0.07 |
| DEF | $0.01 | $0.02 |
| **TOTAL VARIABLE** | **$0.82** | **$1.07** |

---

## Calculating YOUR Total Cost Per Mile

### The Formula

**Cost Per Mile = (Monthly Fixed ÷ Monthly Miles) + Variable Per Mile**

### Example Calculation

**Inputs:**
- Monthly fixed costs: $5,500
- Monthly miles: 10,000
- Variable cost per mile: $0.90

**Calculation:**
- Fixed per mile: $5,500 ÷ 10,000 = $0.55
- Total CPM: $0.55 + $0.90 = **$1.45 per mile**

### Your Calculation

| Your Numbers | Amount |
|--------------|--------|
| Monthly fixed costs | $ |
| Monthly miles (estimate) | miles |
| Fixed per mile | $ |
| Variable per mile | $ |
| **YOUR TOTAL COST PER MILE** | **$** |

---

## What This Means for Load Selection

### Minimum Rate Calculation

**Cost per mile + Desired profit = Minimum rate needed**

| Scenario | CPM | Profit Goal | Minimum Rate |
|----------|-----|-------------|--------------|
| Low overhead | $1.25 | $0.35 | $1.60/mile |
| Average | $1.45 | $0.40 | $1.85/mile |
| High overhead | $1.65 | $0.45 | $2.10/mile |

### True Rate Per Mile (Including Deadhead)

**NEVER forget to include deadhead in your calculation.**

| Load Info | Amount |
|-----------|--------|
| Load rate | $2,800 |
| Loaded miles | 1,200 |
| Posted rate per mile | $2.33 |
| Deadhead to pickup | 180 |
| **Total miles** | 1,380 |
| **TRUE rate per mile** | **$2.03** |

That 180-mile deadhead just cost you $0.30/mile off your rate.

---

## Exercise: Your Personal Cost Analysis

Complete this worksheet with YOUR actual numbers:

### Fixed Costs (Monthly)

| Item | Your Amount |
|------|-------------|
| Truck payment | $ |
| Liability insurance | $ |
| Cargo insurance | $ |
| Physical damage | $ |
| Other insurance | $ |
| Permits (monthly average) | $ |
| Phone | $ |
| ELD | $ |
| Load boards | $ |
| Other | $ |
| **TOTAL MONTHLY FIXED** | **$** |

### Variable Costs (Per Mile)

| Item | Your Amount |
|------|-------------|
| Fuel (price ÷ MPG) | $ |
| Maintenance reserve | $ |
| Tolls (estimate) | $ |
| DEF | $ |
| Other | $ |
| **TOTAL VARIABLE/MILE** | **$** |

### Your True Cost Per Mile

Monthly fixed: $_____ ÷ Monthly miles: _____ = $_____ fixed per mile
Plus variable per mile: + $_____
**YOUR COST PER MILE: $_____**

### Your Minimum Rate

Your CPM: $_____ + Profit goal: $_____ = **Minimum rate: $_____/mile**

---

## Key Takeaways

- Know your cost per mile BEFORE negotiating rates - never guess
- Fixed costs don't change with miles - spread them across more miles for efficiency
- Fuel is your biggest variable - track MPG weekly
- Always calculate true rate per mile including deadhead
- Your minimum rate = CPM + desired profit margin
- Update your CPM calculation monthly as costs change`,
          exercises: [
            {
              type: "calculation",
              title: "Personal Cost Analysis",
              prompt: "Complete the full cost per mile worksheet with your actual numbers. If you don't have a truck yet, use realistic estimates from your research.",
              timeEstimate: "45 minutes",
              deliverable: "Completed cost analysis with your minimum rate"
            }
          ],
          keyTakeaways: [
            "Know your exact cost per mile before negotiating any rate",
            "Fixed costs spread across more miles = lower cost per mile",
            "Fuel is typically 40-50% of your variable costs - track MPG religiously",
            "Always include deadhead when calculating true rate per mile",
            "Update your CPM monthly - costs change constantly"
          ],
          proTips: [
            "Track actual expenses daily for your first 3 months - then you'll have real data",
            "Use a fuel card for easy tracking and potential discounts",
            "Maintenance reserves prevent surprise cash crunches",
            "Know your breakeven - below that, you're paying to work"
          ]
        }
      }
    }
  }
};

export default TRUCKING_BUSINESS_ENHANCED;
